import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";

function ReportsPage() {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month"); // month, week, year, all
  const [notificationCount, setNotificationCount] = useState(0);
  const [selectedPatientId, setSelectedPatientId] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      const loadData = async () => {
        try {
          setLoading(true);

          // Load patients
          const patientsSnap = await getDocs(
            query(
              collection(db, "patients"),
              where("userId", "==", currentUser.uid)
            )
          );
          const patientsData = patientsSnap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }));
          setPatients(patientsData);

          // Load appointments
          const apptsSnap = await getDocs(
            query(
              collection(db, "appointments"),
              where("userId", "==", currentUser.uid)
            )
          );
          const apptsData = apptsSnap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }));
          setAppointments(apptsData);

          // Load notification count
          const notifSnap = await getDocs(
            query(
              collection(db, "notifications"),
              where("userId", "==", currentUser.uid)
            )
          );
          const latestByKey = new Map();
          notifSnap.docs.forEach((d) => {
            const n = { id: d.id, ...d.data() };
            const key = n.appointmentId || n.id;
            const time = n.createdAt?.toMillis ? n.createdAt.toMillis() : 0;
            const prev = latestByKey.get(key);
            const prevTime = prev?.createdAt?.toMillis
              ? prev.createdAt.toMillis()
              : 0;
            if (!prev || time >= prevTime) {
              latestByKey.set(key, n);
            }
          });
          const unreadCount = Array.from(latestByKey.values()).filter(
            (n) => n.read === false
          ).length;
          setNotificationCount(unreadCount);
        } catch (e) {
          console.warn("Failed to load data", e);
        } finally {
          setLoading(false);
        }
      };

      loadData();
    });

    return () => unsubscribe();
  }, []);

  // Calculate earnings based on period
  const patientMap = useMemo(
    () => new Map(patients.map((p) => [p.id, p])),
    [patients]
  );

  const earningsData = useMemo(() => {
    const now = new Date();
    let startDate = new Date();

    switch (period) {
      case "week":
        startDate.setDate(now.getDate() - 7);
        break;
      case "month":
        startDate.setMonth(now.getMonth() - 1);
        break;
      case "year":
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      case "all":
        startDate = new Date(0); // Beginning of time
        break;
      default:
        startDate.setMonth(now.getMonth() - 1);
    }

    const completedAppts = appointments.filter((appt) => {
      const status = String(appt.status || "").toLowerCase();
      if (!status.includes("complete")) return false;

      if (!appt.start) return false;
      const apptDate = appt.start?.toDate
        ? appt.start.toDate()
        : new Date(appt.start);
      return apptDate >= startDate && apptDate <= now;
    });

    const patientFeeMap = new Map();
    patients.forEach((p) => {
      if (p.sessionFee) {
        patientFeeMap.set(p.id, p.sessionFee);
      }
    });

    let totalEarnings = 0;
    let sessionCount = 0;
    const earningsByPatient = new Map();

    completedAppts.forEach((appt) => {
      const patientId = appt.patientId || "unknown";
      const patient = patientMap.get(patientId);
      const fee = patientFeeMap.get(patientId) || 0;

      totalEarnings += fee;
      sessionCount += 1;

      const patientName = appt.patientName || patient?.name || "Unknown";

      const current = earningsByPatient.get(patientId) || {
        patientId,
        name: patientName,
        count: 0,
        total: 0,
        appointments: [],
      };

      const apptDate = appt.start?.toDate
        ? appt.start.toDate()
        : new Date(appt.start);

      earningsByPatient.set(patientId, {
        patientId,
        name: patientName,
        count: current.count + 1,
        total: current.total + fee,
        appointments: [
          ...current.appointments,
          {
            id: appt.id,
            date: apptDate,
            reason: appt.reason || appt.type || "Session",
            status: appt.status || "Completed",
            paymentStatus: appt.paymentStatus || "unpaid",
            fee,
            notes: appt.notes || "",
          },
        ],
      });
    });

    return {
      totalEarnings,
      sessionCount,
      earningsByPatient: Array.from(earningsByPatient.values()).sort(
        (a, b) => b.total - a.total
      ),
      billingByPatient: earningsByPatient,
      completedAppts,
    };
  }, [appointments, patients, patientMap, period]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const formatDateTime = (value) => {
    if (!value) return "";
    const date =
      value instanceof Date
        ? value
        : value?.toDate
        ? value.toDate()
        : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return `${date.toLocaleDateString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
    })} - ${date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })}`;
  };

  const formatPeriod = () => {
    switch (period) {
      case "week":
        return "Last 7 Days";
      case "month":
        return "Last Month";
      case "year":
        return "Last Year";
      case "all":
        return "All Time";
      default:
        return "Last Month";
    }
  };

  if (loading) {
    return (
      <div className="screen">
        <header
          className="form-header"
          style={{
            padding: "16px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <h2>Reports</h2>
        </header>
        <div className="form-card" style={{ border: "none" }}>
          <p className="muted">Loading reports...</p>
        </div>
      </div>
    );
  }

  const selectedPatient = selectedPatientId
    ? earningsData.billingByPatient.get(selectedPatientId)
    : null;

  return (
    <div
      className="screen"
      style={{
        height: "90vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        paddingBottom:0,
      }}
    >
      <header
        className="form-header"
        style={{
          padding: "16px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #e2e8f0",
          flexShrink: 0,
        }}
      >
        <h1 style={{ margin: 0, fontSize: "24px", fontWeight: "600" }}>
          Earnings Report
        </h1>
        <button
          onClick={() => navigate("/notifications")}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "8px",
            position: "relative",
            display: "flex",
            alignItems: "center",
            color: "#64748b",
          }}
          aria-label="Notifications"
        >
          <span className="material-symbols-outlined">notifications</span>
          {notificationCount > 0 && (
            <span
              style={{
                position: "absolute",
                top: "4px",
                right: "4px",
                background: "#ef4444",
                color: "white",
                borderRadius: "10px",
                padding: "2px 6px",
                fontSize: "10px",
                fontWeight: 600,
                minWidth: "16px",
                textAlign: "center",
              }}
            >
              {notificationCount}
            </span>
          )}
        </button>
      </header>

      <div
        className="form-card"
        style={{
          border: "none",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >

        <div style={{ flexShrink: 0 }}>
          <div className="form-section">
            <label className="form-label">Time Period</label>
            <select
              className="form-select"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              <option value="week">Last 7 Days</option>
              <option value="month">Last Month</option>
              <option value="year">Last Year</option>
              <option value="all">All Time</option>
            </select>
          </div>

          {/* Summary Cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: "12px",
              marginTop: "24px",
              marginBottom: "32px",
            }}
          >
            <div
              style={{
                background: "var(--primary-light)",
                border: "1px solid var(--primary)",
                borderRadius: "16px",
                padding: "16px 12px",
                textAlign: "center",
                minWidth: "0",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  fontSize: "clamp(20px, 4vw, 32px)",
                  fontWeight: "800",
                  color: "var(--primary)",
                  marginBottom: "6px",
                  wordBreak: "break-word",
                  lineHeight: "1.2",
                }}
              >
                {formatCurrency(earningsData.totalEarnings)}
              </div>
              <div
                style={{
                  fontSize: "clamp(11px, 2.5vw, 13px)",
                  color: "var(--text-muted)",
                  fontWeight: "600",
                  lineHeight: "1.3",
                  wordBreak: "break-word",
                }}
              >
                Total Earnings ({formatPeriod()})
              </div>
            </div>

            <div
              style={{
                background: "var(--success-light, rgba(16, 185, 129, 0.1))",
                border: "1px solid var(--success, #10b981)",
                borderRadius: "16px",
                padding: "16px 12px",
                textAlign: "center",
                minWidth: "0",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  fontSize: "clamp(20px, 4vw, 32px)",
                  fontWeight: "800",
                  color: "var(--success, #10b981)",
                  marginBottom: "6px",
                  lineHeight: "1.2",
                }}
              >
                {earningsData.sessionCount}
              </div>
              <div
                style={{
                  fontSize: "clamp(11px, 2.5vw, 13px)",
                  color: "var(--text-muted)",
                  fontWeight: "600",
                  lineHeight: "1.3",
                  wordBreak: "break-word",
                }}
              >
                Completed Sessions
              </div>
            </div>

            {earningsData.sessionCount > 0 && (
              <div
                style={{
                  background: "var(--info-light, rgba(59, 130, 246, 0.1))",
                  border: "1px solid var(--info, #3b82f6)",
                  borderRadius: "16px",
                  padding: "16px 12px",
                  textAlign: "center",
                  minWidth: "0",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    fontSize: "clamp(20px, 4vw, 32px)",
                    fontWeight: "800",
                    color: "var(--info, #3b82f6)",
                    marginBottom: "6px",
                    wordBreak: "break-word",
                    lineHeight: "1.2",
                  }}
                >
                  {formatCurrency(
                    earningsData.totalEarnings / earningsData.sessionCount
                  )}
                </div>
                <div
                  style={{
                    fontSize: "clamp(11px, 2.5vw, 13px)",
                    color: "var(--text-muted)",
                    fontWeight: "600",
                    lineHeight: "1.3",
                    wordBreak: "break-word",
                  }}
                >
                  Average per Session
                </div>
              </div>
            )}
          </div>
        </div>


        {earningsData.earningsByPatient.length > 0 && (
          <div
            className="form-section"
            style={{
              marginTop: "0",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div style={{ flexShrink: 0 }}>
              <h3
                style={{
                  fontSize: "clamp(16px, 3vw, 18px)",
                  fontWeight: "800",
                  color: "var(--text-primary)",
                  marginBottom: "16px",
                }}
              >
                Earnings by Patient
              </h3>
              <p
                style={{
                  margin: "0 0 12px",
                  color: "var(--text-muted)",
                  fontSize: "13px",
                  fontWeight: 600,
                }}
              >
                Tap a patient to view their bill breakdown.
              </p>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                flex: 1,
                overflowY: "auto",
                paddingRight: "4px",
                paddingBottom: "20px",
              }}
            >
              {earningsData.earningsByPatient.map((item, idx) => (
                <button
                  key={item.patientId || idx}
                  type="button"
                  onClick={() => setSelectedPatientId(item.patientId)}
                  style={{
                    background: "var(--bg-surface)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "12px",
                    padding: "12px 16px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "12px",
                    flexWrap: "wrap",
                    cursor: "pointer",
                    textAlign: "left",
                    flexShrink: 0,
                  }}
                >
                  <div style={{ flex: "1", minWidth: "0" }}>
                    <div
                      style={{
                        fontSize: "clamp(14px, 3vw, 15px)",
                        fontWeight: "700",
                        color: "var(--text-primary)",
                        marginBottom: "4px",
                        wordBreak: "break-word",
                      }}
                    >
                      {item.name}
                    </div>
                    <div
                      style={{
                        fontSize: "clamp(12px, 2.5vw, 13px)",
                        color: "var(--text-muted)",
                        fontWeight: "600",
                      }}
                    >
                      {item.count} session{item.count !== 1 ? "s" : ""}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: "clamp(16px, 3.5vw, 18px)",
                      fontWeight: "800",
                      color: "var(--primary)",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    {formatCurrency(item.total)}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {earningsData.sessionCount === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "48px 24px",
              marginTop: "32px",
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{
                fontSize: "64px",
                color: "var(--text-muted)",
                marginBottom: "16px",
                display: "block",
              }}
            >
              assessment
            </span>
            <h3
              style={{
                margin: "0 0 8px",
                color: "var(--text-primary)",
                fontSize: "20px",
                fontWeight: "800",
              }}
            >
              No Earnings Data
            </h3>
            <p
              style={{
                color: "var(--text-muted)",
                fontSize: "15px",
                fontWeight: "500",
                margin: "0 0 24px",
                lineHeight: "1.6",
              }}
            >
              {period === "all"
                ? "Complete appointments with billing information to see earnings reports."
                : `No completed appointments found for ${formatPeriod().toLowerCase()}.`}
            </p>
            <button
              type="button"
              onClick={() => navigate("/patients")}
              style={{
                background: "var(--register-primary)",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "12px",
                fontSize: "15px",
                fontWeight: "600",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "20px" }}
              >
                person_add
              </span>
              Set Up Billing
            </button>
          </div>
        )}
      </div>

      {/* Bottom Nav */}
      <nav className="appts-bottom-nav">
        <button className="appts-nav-item" onClick={() => navigate("/doctor")}>
          <span className="material-symbols-outlined">home</span>
          <span>Home</span>
        </button>
        <button
          className="appts-nav-item"
          onClick={() => navigate("/appointments")}
        >
          <span className="material-symbols-outlined">calendar_month</span>
          <span>Appointments</span>
        </button>
        <button
          className="appts-nav-item"
          onClick={() => navigate("/patients")}
        >
          <span className="material-symbols-outlined">groups</span>
          <span>Patients</span>
        </button>
        <button className="appts-nav-item active">
          <span className="material-symbols-outlined fill-current">
            analytics
          </span>
          <span>Reports</span>
        </button>
      </nav>


      {selectedPatient && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
            zIndex: 999,
          }}
          onClick={() => setSelectedPatientId(null)}
        >
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              maxWidth: "720px",
              width: "100%",
              maxHeight: "90vh",
              overflow: "auto",
              boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid var(--border-color)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: 800,
                    color: "var(--text-primary)",
                  }}
                >
                  {selectedPatient.name}
                </div>
                <div
                  style={{
                    color: "var(--text-muted)",
                    fontSize: "13px",
                    fontWeight: 600,
                  }}
                >
                  {selectedPatient.count} session
                  {selectedPatient.count !== 1 ? "s" : ""} - Total{" "}
                  {formatCurrency(selectedPatient.total)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedPatientId(null)}
                style={{
                  border: "none",
                  background: "#eef2ff",
                  color: "#312e81",
                  borderRadius: "10px",
                  padding: "8px 10px",
                  cursor: "pointer",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <span className="material-symbols-outlined">close</span>
                Close
              </button>
            </div>

            <div style={{ padding: "16px 20px" }}>
              {selectedPatient.appointments &&
              selectedPatient.appointments.length > 0 ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  {[...selectedPatient.appointments]
                    .sort(
                      (a, b) =>
                        (b.date?.getTime?.() || 0) - (a.date?.getTime?.() || 0)
                    )
                    .map((appt) => (
                      <div
                        key={appt.id}
                        style={{
                          border: "1px solid var(--border-color)",
                          borderRadius: "12px",
                          padding: "12px 14px",
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                          background: "var(--bg-surface)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: 800,
                              color: "var(--text-primary)",
                            }}
                          >
                            {appt.reason}
                          </div>
                          <div
                            style={{
                              fontWeight: 800,
                              color: "var(--primary)",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatCurrency(appt.fee || 0)}
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                            flexWrap: "wrap",
                            color: "var(--text-muted)",
                            fontSize: "13px",
                            fontWeight: 600,
                          }}
                        >
                          <span>{formatDateTime(appt.date)}</span>
                          <span>|</span>
                          <span>Status: {appt.status}</span>
                          <span>|</span>
                          <span>
                            Payment:{" "}
                            {appt.paymentStatus === "paid" ? "Paid" : "Pending"}
                          </span>
                        </div>
                        {appt.notes && (
                          <div
                            style={{
                              background: "#f8fafc",
                              borderRadius: "8px",
                              padding: "8px",
                              color: "var(--text-muted)",
                              fontSize: "13px",
                              fontWeight: 600,
                            }}
                          >
                            Notes: {appt.notes}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              ) : (
                <p
                  style={{
                    color: "var(--text-muted)",
                    fontSize: "14px",
                    fontWeight: 600,
                    margin: 0,
                  }}
                >
                  No completed appointments found for this patient in the
                  selected period.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReportsPage;